import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { UiDialog } from '@/components/ui/UiDialog';
import { fmtDate } from '@/lib/date';
import type { PlatformCall } from './common';
import { DefaultsApplyDialog } from './DefaultsApplyDialog';

/**
 * Platform defaults: what every new workspace and entity starts with, one
 * sub-tab per module (pay items, perquisite templates, SBI benchmark rates,
 * departments, grades, PT slabs, LWF rules, minimum wages, pay groups, OT
 * rules, shifts, flexi plans, salary structures). Every item says which workspaces it is for (Applies to).
 *
 * Stored in the platform database's `defaults` table, so nothing that happens
 * to a workspace changes them. The initial rows are loaded once from the CLI
 * (`copy-defaults`); here platform operators only add, edit and remove
 * items. A workspace's own items stay in its own database.
 *
 * Every form is built from the module's `/schema` (read off the backend
 * model), so a field added there appears here without a frontend change.
 * Only how a module's list is summarised is configured below.
 */

type Payload = Record<string, unknown>;

interface DefaultRow {
  id: string;
  code: string;
  name: string;
  payload: Payload;
  updated_by: string | null;
  updated_at: string | null;
}

interface FieldSpec {
  name: string;
  kind: 'boolean' | 'integer' | 'number' | 'text' | 'date' | 'json' | 'list';
  nullable: boolean;
  max_length: number | null;
  default: unknown;
  /** The only values allowed (the backend's list), shown as a select. */
  options?: string[];
  item_fields?: FieldSpec[];
}

/** Codes of another module's defaults, offered for a field that refers to them. */
interface Lookup {
  field: string;
  module: string;
  keep?: (p: Payload) => boolean;
}

interface Column {
  label: string;
  value: (p: Payload) => React.ReactNode;
}

interface ModuleView {
  key: string;
  noun: string;
  blurb: string;
  /** Shown first in the form, in this order; every other field follows. */
  lead: string[];
  columns: Column[];
  /** Chips counted by this field (e.g. pay item type). */
  countBy?: string;
  lookups?: Lookup[];
}

const isFlexiPayItem = (p: Payload) =>
  p.is_flexi_eligible === true && String(p.pay_category ?? '').toUpperCase() === 'FLEXI';
const valuedAs = (p: Payload) => String(p.perq_valuation_method ?? '').toUpperCase();
const isPerquisiteHead = (p: Payload) => valuedAs(p) !== '' && valuedAs(p) !== 'LOAN';
const isLoanHead = (p: Payload) => valuedAs(p) === 'LOAN';

const SCOPE_TEXT: Record<string, string> = { ALL: 'All entities', ENTITY: 'Each new entity' };

/** Which workspaces a default is for (every module's Applies to). */
const APPLY_TEXT: Record<string, string> = {
  BOTH: 'All workspaces', STANDALONE: 'Standalone only', UKNOWVA: 'uKnowva only',
};

/** Lowercase option values, worded as the Pay Groups screen words them. */
const OPTION_TEXT: Record<string, string> = {
  ...SCOPE_TEXT,
  ...APPLY_TEXT,
  calendar: 'Calendar (1st to last day)',
  custom: 'Custom (e.g. 26th to 25th)',
  'last-wd': 'Last working day',
  fixed: 'Fixed day',
  pre: 'Previous working day',
  post: 'Next working day',
  exact: 'Exactly on the date',
  manual: 'Manual',
  hybrid: 'Hybrid',
  auto: 'Auto',
};

const label = (name: string) =>
  name.replace(/^is_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\b(Ot|Ctc|Pf|Esic|Pt|Lwf|Fy|Id|Lta|Tds|Sbi)\b/g, (w) => w.toUpperCase());

/** EMPLOYER_CONTRIBUTION → "Employer contribution". */
const enumText = (v: unknown) => {
  const s = typeof v === 'string' ? v.replace(/_/g, ' ') : '';
  return s ? s.charAt(0) + s.slice(1).toLowerCase() : '—';
};

const text = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));
const status = (p: Payload) => (p.is_active === false ? 'Inactive' : 'Active');

function formulaLabel(p: Payload): string {
  const type = String(p.formula_type ?? '');
  const value = p.formula_value as string | null | undefined;
  if (type === 'FIXED') return value ? `Flat ₹${value}` : 'Flat';
  if (type.startsWith('PERCENT_OF_')) {
    return `${Number(value ?? 0)}% of ${type.slice('PERCENT_OF_'.length)}`;
  }
  return enumText(type);
}

function windowLabel(from: unknown, to: unknown): string {
  if (!from && !to) return 'Open';
  return `${from ? fmtDate(String(from)) : 'Any time'} to ${to ? fmtDate(String(to)) : 'any time'}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Which days a pay group's month covers. */
function monthLabel(p: Payload): string {
  const start = Number(p.month_custom_start ?? 1);
  if (p.month_def === 'custom' && start > 1) return `Day ${start} to day ${start - 1}`;
  return '1st to last day';
}

/** The calendar a new entity gets: blanks follow the entity's financial year. */
function calendarLabel(p: Payload): string {
  if (p.generate_calendar === false) return 'Not generated';
  const month = p.calendar_start_month ? MONTHS[Number(p.calendar_start_month) - 1] : 'FY start';
  return `1 year from ${month}, ${p.calendar_year ? String(p.calendar_year) : 'current FY'}`;
}

const appliesCol: Column = {
  label: 'Workspaces', value: (p) => APPLY_TEXT[String(p.apply_to ?? 'BOTH')] ?? text(p.apply_to),
};

/** How an OT rule compensates: "2x hourly rate", "Comp-off 1 day / 8 hrs". */
function otPayLabel(p: Payload): string {
  const mode = String(p.ot_is_compensated_as ?? 'Pay');
  const pay = p.slabwise_ot_rate === true ? 'Slab-wise rate'
    : p.pay_basis === 'Multiplier of Hourly Rate' ? `${Number(p.pay_value ?? 0)}x hourly rate`
      : p.pay_basis ? `${text(p.pay_value)} (${enumText(p.pay_basis)})` : '—';
  const leave = `Comp-off ${enumText(p.comp_off_credit_rule).toLowerCase()}`;
  return mode === 'Leave' ? leave : mode === 'Both' ? `${pay} + ${leave}` : pay;
}

function weekOffLabel(v: unknown): string {
  try {
    const days = (typeof v === 'string' ? JSON.parse(v) : v) as unknown;
    return Array.isArray(days) && days.length ? days.join(', ') : 'None';
  } catch {
    return text(v);
  }
}

const MODULES: ModuleView[] = [
  {
    key: 'PAY_ITEM',
    noun: 'pay item',
    blurb: 'New workspaces start with this list. A new entity gets only the items its workspace lacks.',
    lead: ['short_code', 'name', 'item_type', 'pay_category', 'category',
      'formula_type', 'formula_value', 'formula_expr', 'display_group', 'display_order'],
    countBy: 'item_type',
    columns: [
      { label: 'Code', value: (p) => text(p.short_code) },
      { label: 'Name', value: (p) => text(p.name) },
      { label: 'Type', value: (p) => enumText(p.item_type) },
      { label: 'Pay category', value: (p) => enumText(p.pay_category) },
      { label: 'Formula', value: formulaLabel },
      { label: 'Group', value: (p) => text(p.display_group) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'PERQ_TEMPLATE',
    noun: 'perquisite template',
    blurb: 'The Perquisites master catalog every new workspace starts with.',
    lead: ['code', 'name', 'pay_item_code', 'description', 'default_inputs'],
    countBy: 'perq_type',
    lookups: [{ field: 'pay_item_code', module: 'PAY_ITEM', keep: isPerquisiteHead }],
    columns: [
      { label: 'Code', value: (p) => text(p.code) },
      { label: 'Name', value: (p) => text(p.name) },
      { label: 'Type', value: (p) => enumText(p.perq_type) },
      { label: 'Pay item', value: (p) => text(p.pay_item_code) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'SBI_RATE',
    noun: 'SBI benchmark rate',
    blurb: 'All-entity rates go to every new workspace. Entity rates are copied to each new entity.',
    lead: ['pay_item_code', 'scope', 'rate_pct', 'effective_from', 'effective_to', 'source_note'],
    countBy: 'loan_type',
    lookups: [{ field: 'pay_item_code', module: 'PAY_ITEM', keep: isLoanHead }],
    columns: [
      { label: 'Loan type', value: (p) => enumText(p.loan_type) },
      { label: 'Rate', value: (p) => `${Number(p.rate_pct ?? 0)}%` },
      { label: 'Effective from', value: (p) => fmtDate(p.effective_from as string | null) },
      { label: 'Effective to', value: (p) => (p.effective_to ? fmtDate(String(p.effective_to)) : 'Open') },
      { label: 'Entity', value: (p) => SCOPE_TEXT[String(p.scope)] ?? text(p.scope) },
      { label: 'Source', value: (p) => text(p.source_note) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'DEPARTMENT',
    noun: 'department',
    blurb: 'A starter list: a workspace with no departments yet gets these. One that has its own is left alone.',
    lead: ['code', 'name', 'description', 'head_of_dept'],
    columns: [
      { label: 'Code', value: (p) => text(p.code) },
      { label: 'Name', value: (p) => text(p.name) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'GRADE',
    noun: 'grade',
    blurb: 'A starter list: a workspace with no grades yet gets these. One that has its own is left alone.',
    lead: ['name', 'description'],
    columns: [
      { label: 'Grade', value: (p) => text(p.name) },
      { label: 'Description', value: (p) => text(p.description) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'PT_SLAB',
    noun: 'PT slab',
    blurb: 'A new workspace gets each state\'s slabs as listed here. A new entity adds only states the workspace has no slabs for.',
    lead: ['state_code', 'state_name', 'gender', 'from_amount', 'to_amount', 'monthly_tax',
      'pt_frequency', 'deduction_months', 'month_overrides', 'annual_cap', 'effective_from', 'effective_to'],
    countBy: 'pt_frequency',
    columns: [
      { label: 'State', value: (p) => `${text(p.state_code)} · ${text(p.state_name)}` },
      { label: 'Gender', value: (p) => enumText(p.gender) },
      { label: 'Wage band', value: (p) => `₹${text(p.from_amount)} to ${p.to_amount ? `₹${String(p.to_amount)}` : 'above'}` },
      { label: 'Tax', value: (p) => `₹${text(p.monthly_tax)}` },
      { label: 'Frequency', value: (p) => enumText(p.pt_frequency) },
      { label: 'Effective from', value: (p) => fmtDate(p.effective_from as string | null) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'LWF_RULE',
    noun: 'LWF rule',
    blurb: 'A new workspace gets each state\'s rules as listed here. A new entity adds only states the workspace has no rules for.',
    lead: ['state_code', 'state_name', 'frequency', 'deduction_months', 'calc_type',
      'employee_contribution', 'employer_contribution', 'employee_percent', 'employer_percent',
      'gross_min', 'gross_max', 'effective_from', 'effective_to'],
    columns: [
      { label: 'State', value: (p) => `${text(p.state_code)} · ${text(p.state_name)}` },
      { label: 'Frequency', value: (p) => enumText(p.frequency) },
      { label: 'Employee', value: (p) => `₹${text(p.employee_contribution)}` },
      { label: 'Employer', value: (p) => `₹${text(p.employer_contribution)}` },
      { label: 'Effective from', value: (p) => fmtDate(p.effective_from as string | null) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'MIN_WAGE',
    noun: 'minimum wage',
    blurb: 'A new workspace gets these rates. A new entity adds only a state, skill and industry the workspace has no rates for.',
    lead: ['state_code', 'state_name', 'skill_type', 'industry_category', 'monthly_amount',
      'effective_from', 'effective_to'],
    countBy: 'skill_type',
    columns: [
      { label: 'State', value: (p) => `${text(p.state_code)} · ${text(p.state_name)}` },
      { label: 'Skill', value: (p) => enumText(p.skill_type) },
      { label: 'Industry', value: (p) => text(p.industry_category) },
      { label: 'Monthly', value: (p) => `₹${text(p.monthly_amount)}` },
      { label: 'Effective from', value: (p) => fmtDate(p.effective_from as string | null) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'PAY_GROUP',
    noun: 'pay group',
    blurb: 'Every new entity gets its own copy, with one year of pay calendar. New employees join the primary group.',
    lead: ['name', 'short_code', 'description', 'payroll_cycle', 'month_def', 'month_custom_start',
      'calendar_start_month', 'calendar_year', 'week_off_days', 'payment_day', 'cut_off_day'],
    countBy: 'payroll_cycle',
    columns: [
      { label: 'Name', value: (p) => (p.is_primary === true ? `${text(p.name)} (primary)` : text(p.name)) },
      { label: 'Cycle', value: (p) => enumText(p.payroll_cycle) },
      { label: 'Month', value: monthLabel },
      { label: 'Calendar', value: calendarLabel },
      { label: 'Week off', value: (p) => weekOffLabel(p.week_off_days) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'OT_RULE',
    noun: 'OT rule',
    blurb: 'Every new entity gets its own copy. Rules attached to no shift apply to every shift. Blank Effective from = start of the financial year.',
    lead: ['ot_rule_name', 'ot_trigger', 'status', 'ot_is_compensated_as', 'pay_basis', 'pay_value',
      'payroll_pay_item_code', 'payout_cycle', 'minimum_ot_duration_minutes', 'round_off_ot',
      'max_ot_per_day_hours', 'max_ot_per_month_hours', 'comp_off_credit_rule',
      'credit_to_leave_type_code', 'comp_off_expiry_days', 'max_comp_off_balance',
      'applies_to_pay_group', 'effective_from', 'remarks'],
    countBy: 'status',
    lookups: [
      { field: 'payroll_pay_item_code', module: 'PAY_ITEM', keep: (p) => p.item_type === 'EARNING' },
      { field: 'applies_to_pay_group', module: 'PAY_GROUP' },
    ],
    columns: [
      { label: 'Rule', value: (p) => text(p.ot_rule_name) },
      { label: 'Trigger', value: (p) => enumText(p.ot_trigger) },
      { label: 'Pays', value: otPayLabel },
      { label: 'Pay item', value: (p) => text(p.payroll_pay_item_code) },
      appliesCol,
      { label: 'Status', value: (p) => text(p.status) },
    ],
  },
  {
    key: 'SHIFT',
    noun: 'shift',
    blurb: 'Every new entity gets its own copy. With no break rows, Break Minutes is the unpaid time.',
    lead: ['code', 'name', 'shift_type', 'start_time', 'end_time', 'break_minutes', 'grace_minutes',
      'work_hours_per_day', 'work_days', 'late_after', 'early_before', 'shift_description'],
    columns: [
      { label: 'Code', value: (p) => text(p.code) },
      { label: 'Name', value: (p) => text(p.name) },
      { label: 'Timing', value: (p) => `${text(p.start_time)} to ${text(p.end_time)}${p.is_night_shift === true ? ' (night)' : ''}` },
      { label: 'Paid hours', value: (p) => text(p.paid_hours) },
      { label: 'Grace', value: (p) => `${Number(p.grace_minutes ?? 0)} min` },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'FLEXI_PLAN',
    noun: 'flexi plan',
    blurb: 'Every new entity gets its own copy of these plans. Heads use the default flexi pay items.',
    lead: ['name', 'fiscal_year', 'description', 'annual_budget_amount', 'annual_budget_pct_of_ctc',
      'unspent_treatment', 'declaration_open_from', 'declaration_open_to',
      'proof_window_start', 'proof_window_end', 'country_code'],
    lookups: [{ field: 'pay_item_code', module: 'PAY_ITEM', keep: isFlexiPayItem }],
    columns: [
      { label: 'Plan name', value: (p) => text(p.name) },
      { label: 'FY', value: (p) => text(p.fiscal_year) },
      { label: 'Pay items', value: (p) => (Array.isArray(p.heads) ? p.heads.length : 0) },
      { label: 'Declaration window', value: (p) => windowLabel(p.declaration_open_from, p.declaration_open_to) },
      { label: 'Proof window', value: (p) => windowLabel(p.proof_window_start, p.proof_window_end) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
  {
    key: 'SALARY_STRUCTURE',
    noun: 'salary structure',
    blurb: 'All-entity structures are created once per workspace. The rest are copied to every new entity.',
    lead: ['name', 'scope', 'description', 'grade_band', 'payroll_cycle', 'proration_method',
      'flexi_mode', 'flexi_plan_code', 'effective_from', 'annual_ctc'],
    countBy: 'scope',
    lookups: [
      { field: 'pay_item_code', module: 'PAY_ITEM' },
      { field: 'flexi_plan_code', module: 'FLEXI_PLAN' },
    ],
    columns: [
      { label: 'Name', value: (p) => (p.is_default === true ? `${text(p.name)} (default)` : text(p.name)) },
      { label: 'Entity', value: (p) => SCOPE_TEXT[String(p.scope)] ?? text(p.scope) },
      { label: 'Cycle', value: (p) => enumText(p.payroll_cycle) },
      { label: 'Flexi', value: (p) => (p.flexi_mode === 'WITH_FLEXI' ? text(p.flexi_plan_code) : 'No flexi') },
      { label: 'Components', value: (p) => (Array.isArray(p.components) ? p.components.length : 0) },
      appliesCol,
      { label: 'Status', value: status },
    ],
  },
];

const moduleTitle = (m: ModuleView) => `${m.noun.charAt(0).toUpperCase()}${m.noun.slice(1)}s`;

export function DefaultsTab({ call }: { call: PlatformCall }) {
  const [moduleKey, setModuleKey] = useState(MODULES[0]!.key);
  const [applying, setApplying] = useState(false);
  const view = MODULES.find((m) => m.key === moduleKey) ?? MODULES[0]!;
  return (
    <Box sx={{ maxWidth: 1200 }}>
      <Tabs value={moduleKey} onChange={(_, v: string) => setModuleKey(v)} sx={{ mb: 2 }}
        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
        {MODULES.map((m) => (
          <Tab key={m.key} value={m.key} label={moduleTitle(m)} />
        ))}
      </Tabs>
      <ModuleDefaults key={view.key} call={call} view={view} onApply={() => setApplying(true)} />
      {applying && (
        <DefaultsApplyDialog call={call} initial={view.key}
          modules={MODULES.map((m) => ({ key: m.key, title: moduleTitle(m) }))}
          onClose={() => setApplying(false)} />
      )}
    </Box>
  );
}

function ModuleDefaults({ call, view, onApply }: {
  call: PlatformCall; view: ModuleView; onApply: () => void;
}) {
  const base = `/defaults/${view.key}`;
  const [items, setItems] = useState<DefaultRow[]>([]);
  const [fields, setFields] = useState<FieldSpec[]>([]);
  // Values offered for text fields, by field name (list item fields too).
  const [extraOptions, setExtraOptions] = useState<Record<string, string[]>>({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<DefaultRow | 'new' | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; body: string; run: () => void } | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [list, schema] = await Promise.all([
        call(`${base}/items`) as Promise<{ items: DefaultRow[] }>,
        call(`${base}/schema`) as Promise<{ fields: FieldSpec[] }>,
      ]);
      setItems(list.items);
      setFields(schema.fields);
      // Fields that refer to another module's defaults offer exactly its codes.
      const found: Record<string, string[]> = {};
      for (const l of view.lookups ?? []) {
        const other = (await call(`/defaults/${l.module}/items`)) as { items: DefaultRow[] };
        found[l.field] = other.items.filter((r) => !l.keep || l.keep(r.payload)).map((r) => r.code);
      }
      setExtraOptions(found);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoaded(true);
    }
  }, [call, base, view.lookups]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      setNotice(await fn());
      await load();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () => run(async () => {
    await call(base, { method: 'DELETE' });
    return `Cleared all default ${view.noun}s.`;
  });

  const remove = (it: DefaultRow) => run(async () => {
    await call(`${base}/items/${it.id}`, { method: 'DELETE' });
    return `Removed ${it.code}.`;
  });

  const save = async (payload: Payload) => {
    const target = editing;
    const ok = await run(async () => {
      if (target === 'new') {
        const row = (await call(`${base}/items`, {
          method: 'POST', body: JSON.stringify({ payload }),
        })) as DefaultRow;
        return `Added ${row.code}.`;
      }
      const row = (await call(`${base}/items/${(target as DefaultRow).id}`, {
        method: 'PATCH', body: JSON.stringify({ payload }),
      })) as DefaultRow;
      return `Saved ${row.code}.`;
    });
    if (ok) setEditing(null);
  };

  const summary = useMemo(() => {
    const byValue: Record<string, number> = {};
    let last: DefaultRow | null = null;
    for (const it of items) {
      if (view.countBy) {
        const v = String(it.payload[view.countBy] ?? '');
        byValue[v] = (byValue[v] ?? 0) + 1;
      }
      if (!last || (it.updated_at ?? '') > (last.updated_at ?? '')) last = it;
    }
    return { byValue, last };
  }, [items, view.countBy]);

  // Values already used across the list, per field: offered as suggestions.
  const options = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    const add = (k: string, v: unknown) => {
      if (typeof v === 'string' && v) (out[k] ??= new Set()).add(v);
    };
    for (const it of items) {
      for (const [k, v] of Object.entries(it.payload)) {
        if (Array.isArray(v)) v.forEach((row) => Object.entries((row ?? {}) as Payload).forEach(([rk, rv]) => add(rk, rv)));
        else add(k, v);
      }
    }
    const merged: Record<string, string[]> = {};
    for (const [k, s] of Object.entries(out)) merged[k] = [...s].sort((a, b) => a.localeCompare(b));
    return { ...merged, ...extraOptions };
  }, [items, extraOptions]);

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {err && !editing && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">Default {view.noun}s</Typography>
          <Typography variant="body2" color="text.secondary">{view.blurb}</Typography>
        </Box>
        <Button variant="outlined" disabled={busy || items.length === 0}
          onClick={() => setConfirm({
            title: `Clear all default ${view.noun}s?`,
            body: `New workspaces and entities will no longer get them. Existing workspaces keep what they have.`,
            run: () => void clearAll(),
          })}>
          Clear all
        </Button>
        <Tooltip title="Give existing workspaces these defaults. Never changes what a workspace already has.">
          <span>
            <Button variant="outlined" disabled={busy || items.length === 0} onClick={onApply}>
              Apply to workspaces
            </Button>
          </span>
        </Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} disabled={busy || fields.length === 0}
          onClick={() => setEditing('new')}>
          Add {view.noun}
        </Button>
      </Stack>
      {items.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1.5, flexWrap: 'wrap' }}>
          <Chip size="small" color="primary" label={`${items.length} ${view.noun}s`} />
          {Object.entries(summary.byValue).map(([v, n]) => (
            <Chip key={v} size="small" variant="outlined" label={`${enumText(v)}: ${n}`} />
          ))}
          {summary.last && (
            <Typography variant="caption" color="text.secondary">
              Last changed {fmtDate(summary.last.updated_at)}
              {summary.last.updated_by ? ` by ${summary.last.updated_by}` : ''}
            </Typography>
          )}
        </Stack>
      )}
      <Paper variant="outlined">
        <Box sx={{ maxHeight: 560, overflow: 'auto' }}>
          <TableContainer><Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {view.columns.map((c) => <TableCell key={c.label}>{c.label}</TableCell>)}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={view.columns.length + 1} align="center"
                    sx={{ color: 'text.secondary', py: 3 }}>
                    No default {view.noun}s yet.
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} hover>
                  {view.columns.map((c) => <TableCell key={c.label}>{c.value(it.payload)}</TableCell>)}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" disabled={busy} onClick={() => setEditing(it)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" disabled={busy}
                        onClick={() => setConfirm({
                          title: `Remove ${it.code}?`,
                          body: `New workspaces and entities will no longer get ${it.name}. Workspaces that already have it keep it.`,
                          run: () => void remove(it),
                        })}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
        </Box>
      </Paper>

      {editing && (
        <ItemDialog
          title={editing === 'new' ? `Add default ${view.noun}` : `Edit ${editing.code}`}
          fields={fields}
          lead={view.lead}
          initial={editing === 'new' ? null : editing.payload}
          options={options}
          busy={busy}
          error={err}
          onCancel={() => { setEditing(null); setErr(null); }}
          onSave={(p) => void save(p)}
        />
      )}

      <UiDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        maxWidth="xs"
        dividers={false}
        actions={(
          <>
            <Button variant="outlined" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button color="error" variant="contained" disabled={busy}
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                c?.run();
              }}>
              Confirm
            </Button>
          </>
        )}
      >
        <Typography variant="body2">{confirm?.body}</Typography>
      </UiDialog>
    </Box>
  );
}

const blankOf = (fields: FieldSpec[]): Payload => {
  const out: Payload = {};
  for (const f of fields) if (f.default !== null && f.default !== undefined) out[f.name] = f.default;
  return out;
};

/** One input for one field. Text fields suggest `options[name]`. */
function FieldInput({ f, value, onChange, options, compact }: {
  f: FieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  options: Record<string, string[]>;
  compact?: boolean;
}) {
  const required = !f.nullable && (f.default === null || f.default === undefined);
  const fieldLabel = compact ? undefined : label(f.name);
  if (f.kind === 'boolean') {
    return (
      <FormControlLabel
        control={<Switch size="small" checked={value === true} onChange={(e) => onChange(e.target.checked)} />}
        label={compact ? '' : <Typography variant="body2">{label(f.name)}</Typography>} />
    );
  }
  if (f.kind === 'text' && f.options) {
    return (
      <TextField select size="small" fullWidth label={fieldLabel} required={required}
        aria-label={label(f.name)} value={(value as string | null) ?? ''}
        onChange={(e) => onChange(e.target.value)}>
        {f.options.map((o) => (
          <MenuItem key={o} value={o}>{OPTION_TEXT[o] ?? enumText(o)}</MenuItem>
        ))}
      </TextField>
    );
  }
  if (f.kind === 'text') {
    return (
      <Autocomplete freeSolo size="small" options={options[f.name] ?? []}
        value={(value as string | null) ?? ''}
        onInputChange={(_, v) => onChange(v)}
        renderInput={(p) => (
          <TextField {...p} label={fieldLabel} required={required} aria-label={label(f.name)}
            slotProps={{ htmlInput: { ...p.inputProps, maxLength: f.max_length ?? undefined } }} />
        )} />
    );
  }
  if (f.kind === 'date') {
    return (
      <TextField size="small" fullWidth type="date" label={fieldLabel} required={required}
        aria-label={label(f.name)} value={(value as string | null) ?? ''}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => onChange(e.target.value || null)} />
    );
  }
  if (f.kind === 'json') {
    return (
      <TextField size="small" fullWidth multiline minRows={2} label={fieldLabel} aria-label={label(f.name)}
        defaultValue={value == null ? '' : JSON.stringify(value)}
        onChange={(e) => {
          try { onChange(e.target.value ? JSON.parse(e.target.value) : null); } catch { /* keep typing */ }
        }} />
    );
  }
  return (
    <TextField size="small" fullWidth type="number" label={fieldLabel} required={required}
      aria-label={label(f.name)} value={value == null ? '' : String(value)}
      slotProps={{ htmlInput: { step: f.kind === 'integer' ? 1 : 'any' } }}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} />
  );
}

/** A list field (a plan's heads): one table row per entry. */
function ListInput({ f, value, onChange, options }: {
  f: FieldSpec;
  value: unknown;
  onChange: (v: Payload[]) => void;
  options: Record<string, string[]>;
}) {
  const rows = (Array.isArray(value) ? value : []) as Payload[];
  const cols = f.item_fields ?? [];
  const setRow = (i: number, name: string, v: unknown) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [name]: v } : r)));
  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" sx={{ flex: 1, color: 'text.secondary' }}>{label(f.name)}</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />}
          onClick={() => onChange([...rows, { ...blankOf(cols), sort_order: rows.length }])}>
          Add row
        </Button>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <TableContainer><Table size="small">
          <TableHead>
            <TableRow>
              {cols.map((c) => <TableCell key={c.name} sx={{ minWidth: c.kind === 'boolean' ? 70 : 130 }}>{label(c.name)}</TableCell>)}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols.length + 1} align="center" sx={{ color: 'text.secondary' }}>
                  None yet.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={i}>
                {cols.map((c) => (
                  <TableCell key={c.name}>
                    <FieldInput f={c} value={r[c.name]} options={options} compact
                      onChange={(v) => setRow(i, c.name, v)} />
                  </TableCell>
                ))}
                <TableCell>
                  <IconButton size="small" color="error" aria-label="Remove row"
                    onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer>
      </Box>
    </Box>
  );
}

/** Add / edit one default. Every field comes from the module schema. */
function ItemDialog({ title, fields, lead, initial, options, busy, error, onCancel, onSave }: {
  title: string;
  fields: FieldSpec[];
  lead: string[];
  initial: Payload | null;
  options: Record<string, string[]>;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (p: Payload) => void;
}) {
  const [form, setForm] = useState<Payload>(() => (initial ? { ...initial } : blankOf(fields)));
  const set = (name: string, v: unknown) => setForm((f) => ({ ...f, [name]: v }));

  const ordered = useMemo(() => {
    const byName = new Map(fields.map((f) => [f.name, f]));
    const first = lead.map((n) => byName.get(n)).filter((f): f is FieldSpec => !!f);
    const rest = fields.filter((f) => !lead.includes(f.name));
    return {
      inputs: [...first, ...rest.filter((f) => f.kind !== 'boolean' && f.kind !== 'list')],
      switches: rest.filter((f) => f.kind === 'boolean'),
      lists: fields.filter((f) => f.kind === 'list'),
    };
  }, [fields, lead]);

  return (
    <UiDialog
      open
      onClose={onCancel}
      busy={busy}
      maxWidth="lg"
      title={title}
      actions={(
        <>
          <Button variant="outlined" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => onSave(form)}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        {ordered.inputs.map((f) => (
          <Grid key={f.name} size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldInput f={f} value={form[f.name]} options={options} onChange={(v) => set(f.name, v)} />
          </Grid>
        ))}
      </Grid>
      {ordered.switches.length > 0 && (
        <>
          <Typography variant="overline" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            Flags
          </Typography>
          <Grid container spacing={0.5}>
            {ordered.switches.map((f) => (
              <Grid key={f.name} size={{ xs: 12, sm: 6, md: 4 }}>
                <FieldInput f={f} value={form[f.name]} options={options} onChange={(v) => set(f.name, v)} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {ordered.lists.map((f) => (
        <Box key={f.name} sx={{ mt: 2 }}>
          <ListInput f={f} value={form[f.name]} options={options} onChange={(v) => set(f.name, v)} />
        </Box>
      ))}
    </UiDialog>
  );
}
